import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, UserClub } from '../types';
import { AppHeader, Button, Input, ComboSelect, ScreenLayout, ImageUploader, PageTitle } from '../components';
import { useResponsive } from '../utils/responsive';
import { Colors, Spacing } from '../constants';
import { itemsService } from '../services/items';
import { clubService } from '../services/club';
import { authService } from '../services/auth';
import { optionListsService } from '../services/optionLists';
import { imageService } from '../services/images';
import { draftStorage } from '../utils/draftStorage';
import { handleApiError, showSuccessToast } from '../utils/errorHandler';
import { useDialog } from '../contexts';
import { supabase } from '../config/supabase';

type Props = NativeStackScreenProps<RootStackParamList, 'ItemForm'>;

export const ItemFormScreen: React.FC<Props> = ({ navigation, route }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [size, setSize] = useState('');
  const [category, setCategory] = useState<string>('');
  const [condition, setCondition] = useState<string>('');
  const [sportType, setSportType] = useState<string>('');
  const [selectedClubId, setSelectedClubId] = useState<string>('');

  // Track custom values for option lists
  const [categoryIsCustom, setCategoryIsCustom] = useState(false);
  const [conditionIsCustom, setConditionIsCustom] = useState(false);
  const [sizeIsCustom, setSizeIsCustom] = useState(false);
  const [sportTypeIsCustom, setSportTypeIsCustom] = useState(false);
  const [userClubs, setUserClubs] = useState<UserClub[]>([]);
  const [imagePublicIds, setImagePublicIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [serviceFee, setServiceFee] = useState(1.0);

  // Edit mode state
  const { itemId } = route.params ?? {};
  const isEditMode = !!itemId;
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [titleError, setTitleError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [priceError, setPriceError] = useState('');
  const [sizeError, setSizeError] = useState('');
  const [clubError, setClubError] = useState('');
  const { isDesktop, isWeb, width } = useResponsive();
  const isWideLayout = isWeb && width >= 720;

  useEffect(() => {
    const loadUserClubs = async () => {
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          const clubs = await clubService.getUserClubs(user.id, false); // Fetch full club data including logo_url
          setUserClubs(clubs);

          // Precondition check: If user has 0 clubs, redirect to Onboarding
          if (clubs.length === 0) {
            const shouldNavigate = await dialogRef.current.confirm(
              'Join a Club First',
              'You need to join at least one club before posting items. Would you like to explore clubs now?',
              { variant: 'primary' }
            );

            if (shouldNavigate) {
              navigation.navigate('onboarding');
            } else {
              navigation.goBack();
            }
          }

          // Auto-select club if user has exactly 1 club
          if (clubs.length === 1 && clubs[0].club) {
            setSelectedClubId(clubs[0].club.id);
          }
        }
      } catch (error: any) {
        console.error('[ItemFormScreen] Failed to load user clubs:', error);
      }
    };
    loadUserClubs();
  }, [navigation]);

  // Load existing item for edit mode
  useEffect(() => {
    if (!isEditMode || !itemId) return;

    const loadItem = async () => {
      try {
        const item = await itemsService.getItemById(itemId);
        if (!item) {
          dialogRef.current.alert(t('common.errors.title'), t('screens.itemForm.itemNotFound'), 'error');
          navigation.goBack();
          return;
        }

        // Check ownership
        const user = await authService.getCurrentUser();
        if (item.seller_id !== user?.id) {
          dialogRef.current.alert(t('common.errors.title'), t('screens.itemForm.notOwner'), 'error');
          navigation.goBack();
          return;
        }

        // Check status
        if (item.status !== 'available') {
          dialogRef.current.alert(t('common.errors.title'), t('screens.itemForm.cannotEditStatus'), 'warning');
          navigation.goBack();
          return;
        }

        // Populate form
        setTitle(item.title);
        setDescription(item.description);
        setPrice(item.price.toString());
        setSize(item.size);
        setCategory(item.category);
        setCondition(item.condition);
        setSportType(item.sport_type || '');
        setSelectedClubId(item.club_id);

        // Handle existing images
        if (item.image_urls && item.image_urls.length > 0) {
          setExistingImageUrls(item.image_urls);
          setImagePublicIds(item.image_urls);
        }
      } catch (error: any) {
        handleApiError(error, 'LoadItem');
        navigation.goBack();
      } finally {
        setInitialLoading(false);
      }
    };

    loadItem();
  }, [itemId, isEditMode, t, navigation]);

  // Draft restoration on mount - skip in edit mode
  useEffect(() => {
    if (isEditMode) return; // Skip draft handling when editing

    const loadDraft = async () => {
      try {
        const draft = await draftStorage.getDraft();
        if (draft) {
          // Ask user if they want to restore the draft
          const shouldRestore = await dialogRef.current.confirm(
            t('screens.itemForm.draftFound'),
            t('screens.itemForm.draftFoundMessage'),
            { variant: 'primary' }
          );

          if (shouldRestore) {
            // Restore all form fields
            setTitle(draft.title);
            setDescription(draft.description);
            setPrice(draft.price);
            setSize(draft.size);
            setCategory(draft.category);
            setCondition(draft.condition);
            setSportType(draft.sportType || '');
            // Restore club ID
            setSelectedClubId(draft.clubId || '');

            // Show success message
            dialogRef.current.alert(
              t('screens.itemForm.draftRestored'),
              t('screens.itemForm.draftRestoredMessage'),
              'success'
            );
          } else {
            // User declined - clear the draft
            await draftStorage.clearDraft();
          }
        }
      } catch (error) {
        console.error('[ItemFormScreen] Failed to load draft:', error);
      }
    };
    loadDraft();
  }, [t, isEditMode]);

  // Fetch service fee from admin settings
  useEffect(() => {
    const fetchServiceFee = async () => {
      try {
        const { data: setting } = await supabase
          .from('admin_settings')
          .select('setting_value')
          .eq('setting_key', 'platform_transaction_fee')
          .maybeSingle();

        if (setting?.setting_value) {
          setServiceFee(parseFloat(setting.setting_value));
        }
      } catch (error) {
        console.warn('Failed to load service fee:', error);
      }
    };
    fetchServiceFee();
  }, []);

  // Calculate minimum price based on selected club's transaction fee + service fee
  const selectedClub = useMemo(() => {
    if (!selectedClubId) return null;
    const userClub = userClubs.find(uc => uc.club?.id === selectedClubId);
    return userClub?.club || null;
  }, [selectedClubId, userClubs]);

  const minimumPrice = useMemo(() => {
    return (selectedClub?.transaction_fee_amount || 0) + serviceFee;
  }, [selectedClub, serviceFee]);

  // Clear error when user starts typing
  const handleTitleChange = (text: string) => {
    setTitle(text);
    if (titleError) setTitleError('');
  };

  const handleDescriptionChange = (text: string) => {
    setDescription(text);
    if (descriptionError) setDescriptionError('');
  };

  const handlePriceChange = (text: string) => {
    setPrice(text);
    if (priceError) setPriceError('');
  };


  const handleClubChange = (value: string) => {
    setSelectedClubId(value);
    if (clubError) setClubError('');
  };

  // Validate on blur (when field loses focus)
  const handleTitleBlur = () => {
    if (!title.trim()) {
      setTitleError(t('common.validation.titleRequired'));
    }
  };

  const handleDescriptionBlur = () => {
    if (!description.trim()) {
      setDescriptionError(t('common.validation.descriptionRequired'));
    }
  };

  const handlePriceBlur = () => {
    if (!price.trim()) {
      setPriceError(t('common.validation.priceRequired'));
    } else {
      const priceNumber = parseFloat(price);
      if (isNaN(priceNumber) || priceNumber <= 0) {
        setPriceError(t('common.validation.priceInvalid'));
      }
    }
  };

  const validateFields = () => {
    let isValid = true;

    // Clear previous errors
    setTitleError('');
    setDescriptionError('');
    setPriceError('');
    setSizeError('');
    setClubError('');

    // Validate title
    if (!title.trim()) {
      setTitleError(t('common.validation.titleRequired'));
      isValid = false;
    }

    // Validate description
    if (!description.trim()) {
      setDescriptionError(t('common.validation.descriptionRequired'));
      isValid = false;
    }

    // Validate price
    if (!price.trim()) {
      setPriceError(t('common.validation.priceRequired'));
      isValid = false;
    } else {
      const priceNumber = parseFloat(price);
      if (isNaN(priceNumber) || priceNumber <= 0) {
        setPriceError(t('common.validation.priceInvalid'));
        isValid = false;
      } else if (selectedClubId && priceNumber < minimumPrice) {
        setPriceError(t('screens.itemForm.priceTooLow', { minimum: minimumPrice.toFixed(2) }));
        isValid = false;
      }
    }

    // Validate size
    if (!size.trim()) {
      setSizeError(t('common.validation.sizeRequired'));
      isValid = false;
    }

    // Validate club selection (required)
    if (!selectedClubId) {
      setClubError(t('screens.itemForm.validation.selectClub.message'));
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async () => {
    // Validate fields
    if (!validateFields()) {
      return;
    }

    const priceNumber = parseFloat(price);

    setLoading(true);
    try {
      // STEP 1: Save draft before submission (defensive protection, create mode only)
      if (!isEditMode) {
        await draftStorage.saveDraft({
          title,
          description,
          price,
          size,
          category,
          condition,
          sportType: sportType || undefined,
          clubId: selectedClubId,
        });
      }

      // STEP 2: Track custom values if used
      if (categoryIsCustom) {
        await optionListsService.trackCustomValue('category', category);
      }
      if (conditionIsCustom) {
        await optionListsService.trackCustomValue('condition', condition);
      }
      if (sizeIsCustom) {
        await optionListsService.trackCustomValue('size', size);
      }
      if (sportTypeIsCustom && sportType) {
        await optionListsService.trackCustomValue('sport_type', sportType);
      }

      // STEP 3: Create or update the item
      if (isEditMode && itemId) {
        // Update existing item
        await itemsService.updateItem(itemId, {
          title,
          description,
          price: priceNumber,
          size,
          category,
          condition,
          sport_type: sportType || undefined,
          club_id: selectedClubId,
          image_urls: imagePublicIds.length > 0 ? imagePublicIds : undefined,
        });

        showSuccessToast(
          t('screens.itemForm.updateSuccess'),
          t('screens.itemForm.updateSuccessMessage')
        );

        // Navigate back to item details
        setTimeout(() => {
          navigation.goBack();
        }, 500);
      } else {
        // Create new item
        await itemsService.createItem({
          title,
          description,
          price: priceNumber,
          size,
          category,
          condition,
          sport_type: sportType || undefined,
          club_id: selectedClubId,
          image_urls: imagePublicIds.length > 0 ? imagePublicIds : undefined,
        });

        // STEP 4: Clear draft on success
        await draftStorage.clearDraft();

        showSuccessToast(
          t('screens.itemForm.success'),
          t('screens.itemForm.successMessage')
        );

        // Navigate to home after short delay
        setTimeout(() => {
          navigation.navigate('Home');
        }, 500);
      }
    } catch (error: any) {
      // Use centralized error handler (shows toast for session expiry)
      handleApiError(error, isEditMode ? 'UpdateListing' : 'CreateListing');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <ScreenLayout fullWidth={true}>
        <View style={styles.outerContainer}>
          {!isDesktop && (
            <AppHeader
              navigation={navigation}
              currentScreen="Home"
              title={t('screens.itemForm.editTitle')}
              showBackButton={true}
              hideMenu={true}
            />
          )}
          <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
            <Text>{t('common.loading')}</Text>
          </View>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout maxWidth={800} desktopPadding>
      <View style={styles.outerContainer}>
        {!isDesktop && (
          <AppHeader
            navigation={navigation}
            currentScreen="Home"
            title={isEditMode ? t('screens.itemForm.editTitle') : t('screens.itemForm.title')}
            showBackButton={true}
          />
        )}
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.container}
        >
          <ScrollView contentContainerStyle={styles.scrollContent}>
            <View style={[styles.formWrapper, isDesktop && styles.formWrapperWeb, isWideLayout && styles.formWrapperWide]}>
              {isDesktop && (
                <PageTitle
                  title={isEditMode ? t('screens.itemForm.editTitle') : t('screens.itemForm.title')}
                  subtitle={isEditMode ? undefined : t('screens.itemForm.subtitle')}
                  onBack={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Home')}
                />
              )}

              <ImageUploader
                maxImages={2}
                onImagesUploaded={setImagePublicIds}
                initialImages={existingImageUrls}
              />

        <Input
          label={`${t('screens.itemForm.titleLabel')} *`}
          value={title}
          onChangeText={handleTitleChange}
          onBlur={handleTitleBlur}
          placeholder={t('screens.itemForm.titlePlaceholder')}
          error={titleError}
        />

        <Input
          label={`${t('screens.itemForm.description')} *`}
          value={description}
          onChangeText={handleDescriptionChange}
          onBlur={handleDescriptionBlur}
          placeholder={t('screens.itemForm.descriptionPlaceholder')}
          multiline
          numberOfLines={6}
          inputStyle={styles.textArea}
          error={descriptionError}
        />

        <View style={[styles.rowContainer, isWideLayout && styles.rowContainerWide]}>
          <Input
            label={`${t('screens.itemForm.price')} (€) *`}
            value={price}
            onChangeText={handlePriceChange}
            onBlur={handlePriceBlur}
            placeholder={t('screens.itemForm.pricePlaceholder')}
            keyboardType="decimal-pad"
            error={priceError}
            helperText={selectedClubId && minimumPrice > 0
              ? t('screens.itemForm.minimumPriceHint', { minimum: minimumPrice.toFixed(2) })
              : undefined}
            style={[styles.rowInput, isWideLayout && styles.rowInputWide]}
          />

          <ComboSelect
            label={`${t('screens.itemForm.size')} *`}
            listType="size"
            value={size}
            onValueChange={(val, isCustom) => {
              setSize(val);
              setSizeIsCustom(isCustom);
              if (sizeError) setSizeError('');
            }}
            allowCustom={true}
            searchPlaceholder={t('components.comboSelect.searchPlaceholder', { type: t('components.comboSelect.listTypes.size') })}
            error={sizeError}
            style={[styles.rowInput, isWideLayout && styles.rowInputWide]}
          />
        </View>

        <View style={[styles.rowContainer, isWideLayout && styles.rowContainerWide]}>
          <ComboSelect
            label={`${t('screens.itemForm.category')} *`}
            listType="category"
            value={category}
            onValueChange={(val, isCustom) => {
              setCategory(val);
              setCategoryIsCustom(isCustom);
            }}
            allowCustom={true}
            searchPlaceholder={t('components.comboSelect.searchPlaceholder', { type: t('components.comboSelect.listTypes.category') })}
            style={[styles.rowInput, isWideLayout && styles.rowInputWide]}
          />

          <ComboSelect
            label={`${t('screens.itemForm.condition')} *`}
            listType="condition"
            value={condition}
            onValueChange={(val, isCustom) => {
              setCondition(val);
              setConditionIsCustom(isCustom);
            }}
            allowCustom={true}
            searchPlaceholder={t('components.comboSelect.searchPlaceholder', { type: t('components.comboSelect.listTypes.condition') })}
            style={[styles.rowInput, isWideLayout && styles.rowInputWide]}
          />
        </View>

        <View style={[styles.rowContainer, isWideLayout && styles.rowContainerWide]}>
          {userClubs.length > 0 && (
            <ComboSelect
              label={`${t('screens.itemForm.form.club.label')} *`}
              customOptions={[...userClubs]
                .sort((a, b) => {
                  const nameA = a.club?.name?.toLowerCase() || '';
                  const nameB = b.club?.name?.toLowerCase() || '';
                  return nameA.localeCompare(nameB);
                })
                .map(uc => ({
                  label: uc.club!.name,
                  value: uc.club!.id,
                  imageUrl: uc.club!.logo_url
                    ? imageService.getLogoUrl(uc.club!.logo_url, 'small')
                    : undefined,
                }))}
              value={selectedClubId}
              onValueChange={(val) => handleClubChange(val)}
              allowCustom={false}
              placeholder={t('screens.itemForm.form.club.placeholder')}
              searchPlaceholder={t('screens.clubs.searchPlaceholder')}
              error={clubError}
              style={[styles.rowInput, isWideLayout && styles.rowInputWide]}
            />
          )}

          <ComboSelect
            label={t('screens.itemForm.sportType')}
            listType="sport_type"
            value={sportType}
            onValueChange={(val, isCustom) => {
              setSportType(val);
              setSportTypeIsCustom(isCustom);
            }}
            allowCustom={true}
            placeholder={t('screens.itemForm.sportTypePlaceholder')}
            searchPlaceholder={t('components.comboSelect.searchPlaceholder', { type: t('components.comboSelect.listTypes.sport_type') })}
            style={[styles.rowInput, isWideLayout && styles.rowInputWide]}
          />
        </View>

              <View style={[styles.buttonContainer, isWeb && styles.buttonContainerWide]}>
                <Button
                  title={isEditMode ? t('screens.itemForm.saveChanges') : t('screens.itemForm.createListing')}
                  onPress={handleSubmit}
                  loading={loading}
                  style={[styles.submitButton, isWeb && styles.submitButtonWide]}
                />
                <Button
                  title={t('common.buttons.cancel')}
                  onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Profile')}
                  variant="outline"
                  style={[styles.secondaryButton, isWeb && styles.secondaryButtonWide]}
                />
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  formWrapper: {
    paddingVertical: Spacing.base,
    paddingHorizontal: Spacing.base,
    width: '100%',
  },
  formWrapperWeb: {
    paddingVertical: 0,
    paddingHorizontal: Spacing.xl,
  },
  formWrapperWide: {
    paddingHorizontal: Spacing.xl,
  },
  textArea: {
    height: 120,
    textAlignVertical: 'top',
    paddingTop: 12,
    paddingBottom: 12,
  },
  rowContainer: {
    width: '100%',
  },
  rowContainerWide: {
    flexDirection: 'row',
    gap: 16,
  },
  rowInput: {},
  rowInputWide: {
    flex: 1,
  },
  buttonContainer: {
    width: '100%',
  },
  buttonContainerWide: {
    flexDirection: 'row',
    gap: 16,
  },
  submitButton: {
    ...(Platform.OS !== 'web' && {
      marginBottom: 16,
    }),
  },
  submitButtonWide: {
    flex: 1,
  },
  secondaryButton: {},
  secondaryButtonWide: {
    flex: 0,
    minWidth: 150,
    maxWidth: 200,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text,
    marginTop: 16,
    marginBottom: 8,
  },
});
